import React from "react"
import { AdvancedMarker, useApiIsLoaded, useMap } from "@vis.gl/react-google-maps"
import Supercluster from "supercluster"
import { BikeMarker } from "./BikeMarker"
import { BikesClusterMarker } from "./BikesClusterMarker"
import { MapBikeEntity } from "../lib/hooks/queries/useGetMapBikesQuery"
import { BikesCluster, calcClusterStatePercentage } from "../lib/helpers/clusters"
import { getBounds } from "../lib/helpers/map"
import { createGoogleMapsBoundsFromBounds } from "../lib/helpers/googleMap"

interface BikesMapGoogleSuperclusterMarkerProps {
  mapId: string
  currentCluster: BikesCluster
  superClusterInstance: Supercluster | undefined
  activeBike: MapBikeEntity | null
}

const _BikesMapGoogleSuperclusterMarker: React.FC<BikesMapGoogleSuperclusterMarkerProps> = ({
  mapId,
  currentCluster,
  superClusterInstance,
  activeBike,
}) => {
  const map = useMap(mapId)
  const apiIsLoaded = useApiIsLoaded()

  const isCluster = currentCluster.properties.cluster
  const isActive = activeBike?.uuid === currentCluster.properties.bicycle?.uuid
  const { uuids, stateCounts } = calcClusterStatePercentage({ superClusterInstance, currentCluster })

  const onMarkerClusterClick = React.useCallback(() => {
    const { cluster_id } = currentCluster.properties
    if (!map || !superClusterInstance || !cluster_id || !apiIsLoaded) return
    const points = superClusterInstance
      .getLeaves(cluster_id, Infinity)
      .map((leaf: any) => leaf.geometry.coordinates)
    const bounds = getBounds(points)
    const gMapBounds = createGoogleMapsBoundsFromBounds(bounds)
    map?.fitBounds(gMapBounds)
  }, [map, currentCluster, superClusterInstance, apiIsLoaded])

  return (
    <AdvancedMarker
      position={{
        lat: currentCluster.geometry.coordinates[1],
        lng: currentCluster.geometry.coordinates[0],
      }}
      // needs a click listener as workaround to make marker child focusable
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      onClick={() => {}}
    >
      {isCluster ? (
        <BikesClusterMarker
          count={currentCluster.properties?.point_count ?? 0}
          stateCounts={stateCounts}
          uuids={uuids}
          onClick={onMarkerClusterClick}
        />
      ) : (
        <BikeMarker bike={currentCluster.properties.bicycle!} isActive={isActive} />
      )}
    </AdvancedMarker>
  )
}

const BikesMapGoogleSuperclusterMarker = React.memo(_BikesMapGoogleSuperclusterMarker)

export default BikesMapGoogleSuperclusterMarker
